import { createContext, useState, useEffect, useRef } from "react";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import moment from "moment";
import { axiosJWT, axiosSC, axiosSW, axiosSW2 } from "../api/axiosDefaults";
import useLocalTheme from "../hooks/useLocalTheme";
import { hexToRGB, isTokenExpired } from "../utils/utils";
import { env } from "../env";

import customizationsDefault from "../assets/customizationsDefault.json";
import useLocalStorage from "../hooks/useLocalStorage";
import useSessionStorage from "../hooks/useSessionStorage";
import useAuthReady from "../hooks/useAuthReady";

const DataContext = createContext({});

export const DataProvider = ({ children }) => {
    // this is the offset that is added to the footer height to calculate
    // the height of the payment header and footer
    // used in home and not found pages (entry point pages)
    // const FOOTER_OFFSET = 184;
    // const FOOTER_OFFSET = 203;
    const FOOTER_OFFSET = 194;

    const [responseData, setResponseData] = useState(null);

    const [currentCustom, setCurrentCustom] = useSessionStorage(
        "currentCustom",
        customizationsDefault,
    );
    const [connection, setConnection] = useSessionStorage("connection", null);
    const [languages, setLanguages] = useSessionStorage("languages", []);
    const [localizations, setLocalizations] = useSessionStorage(
        "localizations",
        [],
    );
    const [JWTToken, setJWTToken] = useLocalStorage("JWTToken", null);
    const [JWTRefreshToken, setJWTRefreshToken] = useLocalStorage(
        "JWTRefreshToken",
        null,
    );
    const [currentBU, setCurrentBU] = useSessionStorage("currentBU", null);

    const [isServiceDataAvailable, setIsServiceDataAvailable] = useState(false);
    const [islocalizationsAvailable, setIslocalizationsAvailable] =
        useState(false);

    const [homePath, setHomePath] = useSessionStorage("homePath", null);

    const [currentTicket, setCurrentTicket] = useState(null);
    const [axiosReady, setAxiosReady] = useState(false);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [selectedInstalments, setSelectedInstalments] = useState([]);
    const [isInstallemtHover, setIsInstallemtHover] = useState("");
    const [isrelatedRedirect, setIsrelatedRedirect] = useState(false);

    const [ticketType, setTicketType] = useState(null);
    const [currentCategory, setCurrentCategory] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isValidSearch, setIsValidSearch] = useState(false);
    const [isServerDown, setIsServerDown] = useState(false);

    const [result, setResult] = useState(null);
    const [searchedTicket, setSearchedTicket] = useState(null);
    // const [defaultLanguage, setDefaultLanguage] = useState("en");
    const [reCaptchaReady, setReCaptchaReady] = useState(false);
    const [initialReCaptcha, setInitialReCaptcha] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [isPayHover, setIsPayHover] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentHeaderFooterHeight, setPaymentHeaderFooterHeight] =
        useState(0);

    const navigate = useNavigate();
    const theme = useLocalTheme();
    const location = useLocation();

    /**
     * Authinticat the API for the application using env variables
     * and set the JWTtoken and refreshJWTtoken on page mount
     * if authintication fails redirect to schweers web page
     * @returns {Object} - the response object
     */

    // Fix for env.js visibility by migrating the name and password to recaptcha server
    // const APIAuthenticate = async () => {
    //     try {
    //         const { data } = await axiosSC.post(
    //             `${env.REACT_APP_CAPTCHA_STRIPE_API_URL}/api/schweers/authenticate`,
    //         );

    //         if (data) {
    //             setJWTToken(data?.accessToken);
    //             setJWTRefreshToken(data?.refreshToken);
    //         }
    //         return data;
    //     } catch (error) {
    //         console.log("API authentication failed:", error);
    //         navigate("/");
    //     }
    // };

    const APIAuthenticate = async () => {
        try {
            const { data } = await axiosJWT.post("/Authentication/Login", {
                username: env.REACT_APP_SCHWEERS_API_USER_NAME,
                password: env.REACT_APP_SCHWEERS_API_USER_PASSWORD,
            });

            if (data) {
                // Write synchronously so the axios interceptors (which read
                // localStorage directly) see the new token immediately.
                localStorage.setItem(
                    "JWTToken",
                    JSON.stringify(data.accessToken),
                );
                localStorage.setItem(
                    "JWTRefreshToken",
                    JSON.stringify(data.refreshToken),
                );
                setJWTToken(data?.accessToken);
                setJWTRefreshToken(data?.refreshToken);
            }
            return data;
        } catch (error) {
            console.log("API authentication failed:", error);
            navigate("/");
        }
    };

    // excute APIAuthenticate() on mount
    useEffect(() => {
        if (!JWTToken || !JWTRefreshToken || isTokenExpired(JWTToken)) {
            APIAuthenticate();
        }
    }, []);

    // check if the service data is available, and if not fetch it
    useEffect(() => {
        if (
            connection &&
            currentCustom &&
            localizations &&
            localizations.length > 0
        ) {
            setIsServiceDataAvailable(true);
        }
        // ############################################# testing
        // else {
        //     currentBU && serviceData(currentBU);
        // }
        // ############################################# testing
    }, [
        navigate,
        currentBU,
        connection,
        currentCustom,
        localizations,
        location.pathname,
    ]);

    // set the home path based on the current BU, check if the url contains "/pos" and set the home path accordingly
    useEffect(() => {
        if (location && location.pathname.includes("/pos")) {
            setHomePath(`/${currentBU}/pos`);

            // setRecaptchaOffset("50px"); // Set the recaptcha offset
        } else {
            setHomePath(`/${currentBU}`);

            // setRecaptchaOffset("14px"); // Set the recaptcha offset
        }
    }, [currentBU, location.pathname]);

    // set the main color based on the current custom theme, and the default language based on the user culture
    useEffect(() => {
        if (currentCustom) {
            setMainColor(currentCustom?.color?.current);
            setErroColor(currentCustom?.color?.error);
        }
        // if (connection?.culture) {
        //     const userLanguage = connection?.culture;

        //     userLanguage && setDefaultLanguage(userLanguage.split("-")[0]);
        // }
    }, [currentCustom, connection?.culture]);

    //Google recaptcha script initialization, add the script to the head of the document
    useEffect(() => {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${env.REACT_APP_GOOGLE_SITE_KEY}`;

        script.onload = () => {
            if (window.grecaptcha) {
                window.grecaptcha.ready(() => {
                    setReCaptchaReady(true);
                });
            }
        };
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    /**
     * Verify the reCAPTCHA token and return the result
     * @returns {Boolean} - returns true if the reCAPTCHA verification is successful
     */
    const verifyReCaptcha = async () => {
        if (!reCaptchaReady) return;
        setIsLoading(true);
        try {
            const token = await window.grecaptcha.execute(
                env.REACT_APP_GOOGLE_SITE_KEY,
                { action: "ticket_search" },
            );
            if (!token) {
                console.error("Failed to retrieve reCAPTCHA token");
                return false;
            }

            const { data } = await axiosSC.post("/api/verify-recaptcha", {
                token,
            });

            // -------------------------------------------------------------------------------- the correct logic

            if (data.success) {
                // setReCaptchaVerification(true);

                setIsLoading(false);
                return true;
            } else {
                throw new Error("Failed reCAPTCHA verification");
            }

            // -------------------------------------------------------------------------------- the correct logic

            // ----------------------------------------------------------------------------- testing
            // if (data) {
            //     setIsLoading(false);
            //     return true;
            // }
            // ----------------------------------------------------------------------------- testing
        } catch (error) {
            console.error("reCAPTCHA error:", error);
            setIsLoading(false);
            handleError(error);
        }
    };

    /**
     * Error handling function for forwarding and logging the error that occurs
     * @param {*} error - object that contains the error information
     */
    const handleError = (error) => {
        const errorLog = {
            userID: connection?.customerID ? connection?.customerID : -1,
            searchedTicket: searchedTicket ? searchedTicket : "",
            status: error?.response?.status ? error?.response?.status : -1,
            message: error?.message ? error?.message : "",
            code: error?.code ? error?.code : "",
            timeStamp: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
            config: {
                method: error?.config?.method ? error?.config?.method : "",
                url: error?.config?.url ? error?.config?.url : "",
                data: error?.config?.data ? error?.config?.data : "",
            },
        };
        console.log("error:", error);

        // submitError(errorLog);
        const errorObj = {
            errorLog: errorLog,
            error: error,
            errorMessage: error?.message,
        };
        setIsLoading(false);
        setResult(errorObj);
        setIsServerDown(true);

        navigate(`${homePath}/result`);
    };

    /**
     * Fetch the service data for the current, and set the connection, customizations, localizations and languages accordingly
     * @param {string} customer - the customer id
     */
    const serviceData = async (customer) => {
        // If the stored token is expired/missing, re-authenticate BEFORE the
        // configuration request goes out — avoids the 401 → refresh → reload
        // fix the technical error on start
        if (isTokenExpired(JWTToken)) {
            await APIAuthenticate();
        }

        // Extract the languages from the localizations array

        /**
         * Extract the languages from the localizations array and sort them so that the default language is always first
         * @param {Array} array = the array of localizations
         * @returns {Array} - returns an array of languages
         */
        const getLanguages = (array) => {
            let languages = [];
            array.map((lang) => {
                const currentLanguage = lang?.culture;
                languages.push(currentLanguage);
            });
            // sort the languages array so that the default language is always first
            languages.sort((a, b) =>
                a.split("-")[0] === "en" ? -1 : b === "en" ? 1 : 0,
            );
            setLanguages(languages);

            return languages;
        };

        const getProduct = (productCode, products) => {
            if (!products || !Array.isArray(products)) {
                return false;
            }
            const exists = products.find((p) => p.code === productCode);
            return exists;
        };

        /**
         * Check if the client is allowed to access the current module (POS or Payment) and redirect if not
         * @param {Object} response
         */
        const checkValidModule = (response, isPOS, currentProduct) => {
            if (isPOS && !response?.customerConfiguration?.isPolitessPOS) {
                navigate(`/`);
            }
            if (!isPOS && !Boolean(currentProduct)) {
                navigate(`/`);
            }
        };
        const checkConnection = (response, isPOS, currentProduct) => {
            if (isPOS) {
                setConnection(response?.customerConfiguration);
            } else {
                const tempConnection = {
                    ...response?.customer,
                    ...(currentProduct?.settings ?? {}),
                    productInfo: {
                        code: currentProduct?.code,
                        isEnabled: currentProduct?.isEnabled,
                    },
                };

                setConnection(tempConnection);
            }
        };

        const checkCurrentCustom = (response, isPOS, currentProduct) => {
            if (isPOS) {
                setCurrentCustom(
                    response?.customerCustomization &&
                        response?.customerCustomization?.customizationContent
                        ? JSON.parse(
                              response?.customerCustomization
                                  ?.customizationContent,
                          )
                        : customizationsDefault,
                );
            } else {
                setCurrentCustom(
                    currentProduct?.customization
                        ? JSON.parse(currentProduct?.customization)
                        : customizationsDefault,
                );
            }
        };

        const checkLocalizations = (response, isPOS, currentProduct) => {
            if (isPOS) {
                setLocalizations(response?.customerLocalizations);
            } else {
                const currentLocalizations = currentProduct?.localizations
                    ? currentProduct?.localizations.map(
                          ({ cultureCode, ...rest }) => ({
                              culture: cultureCode,
                              ...rest,
                          }),
                      )
                    : [];
                setLocalizations(currentLocalizations);
            }
        };

        const checkLanguages = (response, isPOS, currentProduct) => {
            if (isPOS) {
                getLanguages(response?.customerLocalizations);
            } else {
                const currentLocalizations = currentProduct?.localizations
                    ? currentProduct?.localizations.map(
                          ({ cultureCode, ...rest }) => ({
                              culture: cultureCode,
                              ...rest,
                          }),
                      )
                    : [];
                getLanguages(currentLocalizations);
            }
        };

        const isPOS =
            location && location.pathname.includes("/pos") ? true : false;

        // Fetch data from different endpoints based on the current location
        setIsLoading(true);
        try {
            if (customer) {
                let response;

                if (isPOS) {
                    const { data } = await axiosSW.get(
                        `Configuration/pos/getCustomerConfiguration/${customer}`,
                    );

                    response = data;
                } else {
                    const { data } = await axiosSW2.get(
                        `customers/${customer}/configuration`,
                    );
                    response = data;
                }

                const currentProduct = response?.products
                    ? getProduct("PolitessPay", response?.products)
                    : undefined;

                checkValidModule(response, isPOS, currentProduct);

                checkConnection(response, isPOS, currentProduct);

                // setConnection(response?.customerConfiguration);
                checkCurrentCustom(response, isPOS, currentProduct);
                // setCurrentCustom(
                //     response?.customerCustomization &&
                //         response?.customerCustomization?.customizationContent
                //         ? JSON.parse(
                //               response?.customerCustomization
                //                   ?.customizationContent,
                //           )
                //         : customizationsDefault,
                // );
                checkLocalizations(response, isPOS, currentProduct);
                // setLocalizations(response?.customerLocalizations);
                checkLanguages(response, isPOS, currentProduct);
                // getLanguages(response?.customerLocalizations);
                setIsServiceDataAvailable(true);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);

            // redirect to the home page if error is 404
            // with no service data the application will not function proporly
            // the header and the footer content, the theme color, etc...
            // and from there will be redirected to the company web site
            // if(error?.response?.status === 404 || error?.response?.status === -1 || error?.response?.status === 0) {
            if (
                error?.response?.status === 404 ||
                error?.response?.status === 400
            ) {
                navigate(`/`);
            } else {
                setIsServerDown(true);

                handleError(error);
            }
        }
    };

    /**
     * Sets the main theme color variable in css
     */
    const setMainColor = (color) => {
        const root = document.documentElement;

        root.style.setProperty("--current-color", hexToRGB(color));
    };
    const setErroColor = (color) => {
        const root = document.documentElement;

        root.style.setProperty("--error-color", hexToRGB(color));
    };

    /**
     * Sets the recaptcha offset variable in css
     */
    const setRecaptchaOffset = (offset) => {
        const root = document.documentElement;

        root.style.setProperty("--recaptcha-offset", offset);
    };

    // ##################################################################################### Testing
    /**
     * Functions that need to be invoked at the start
     */
    const handleMount = () => {};

    // useEffect(() => {
    //     handleMount();
    // }, []);
    // ##################################################################################### Testing

    return (
        <DataContext.Provider
            value={{
                responseData, // {Object} response data from the server on fetch tickets
                setResponseData,

                currentCustom, // {Object} current custom theme object

                connection, // {Object} object that contains the business user data

                currentTicket, // {Object} current ticket object
                setCurrentTicket,

                ticketType,
                setTicketType,

                isLoading, // {Boolean} loading state
                setIsLoading,

                currentCategory, // {String} current category from ticket photos (set to first category by default)
                setCurrentCategory,

                isValidSearch, // {Boolean} search validity state
                setIsValidSearch,

                result, // {Object} result object (used in the result page)
                setResult,

                handleError, // {Function} error handling function

                searchedTicket, // {String} searched ticket number
                setSearchedTicket,

                theme, // {String} theme used (light or dark)

                languages, // {Array} languages available (taken from localization)

                // defaultLanguage, // {String} default language based on user culture

                localizations, // {Array} localizations available (array of objects)

                reCaptchaReady, // {Boolean} reCAPTCHA script ready state

                homePath, // {String} home path based on the current BU
                setHomePath,

                currentBU, // {String} current buissness user (customer)
                setCurrentBU,

                serviceData, // {Function} fetch service data function

                isServiceDataAvailable, // {Boolean} service data availability state
                setIsServiceDataAvailable,

                verifyReCaptcha, // {Function} verify reCAPTCHA token function

                // initialReCaptcha, // Initial reCAPTCHA verification state currently not used

                islocalizationsAvailable, // {Boolean} localizations availability state
                setIslocalizationsAvailable,

                isRedirecting, // {Boolean} redirecting state
                setIsRedirecting,

                paymentHeaderFooterHeight, // {Float} The calculated of the header and footer (this is only used in the payment module)
                // to prevent the footer from jumping up when page content height is smaller than the screen height
                // this is calculated in the home and not found pages
                setPaymentHeaderFooterHeight,

                FOOTER_OFFSET, // {integer} The offset (constant) that is added to the footer height (margin, padding, etc...)

                isServerDown,
                setIsServerDown,
                selectedTickets,
                setSelectedTickets,
                selectedInstalments,
                setSelectedInstalments,
                isPayHover,
                setIsPayHover,
                isInstallemtHover,
                setIsInstallemtHover,
                isrelatedRedirect,
                setIsrelatedRedirect,

                showPaymentForm,
                setShowPaymentForm,
                APIAuthenticate,
                JWTToken,
                JWTRefreshToken,
                axiosReady,
                setAxiosReady,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default DataContext;
